import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import {
  GET_BASE_SCOPES_PARAMETERS,
  GET_COMPONENTS,
  GET_CUSTOM_SCOPES,
  GET_CUSTOM_SCOPES_PARAMETERS,
} from '../../GraphQL/ManageCustomScopes/Queries';
import './ViewCustomScopes.css';
import {
  CREATE_CUSTOM_SCOPE,
  DELETE_CUSTOM_SCOPE,
  EDIT_CUSTOM_SCOPE,
} from '../../GraphQL/ManageCustomScopes/Mutations';
import { Button } from 'primereact/button';
import GraphQLWrapper from '../UI/GraphQLWrapper/GraphQLWrapper';
import CustomModal from '../UI/Modal/Modal';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import BaseScopeParametersGrid from './BaseScopeParametersGrid';

function ViewCustomScopes() {
  const [components, setComponents] = useState([]);
  const [parameters, setParameters] = useState({});
  const [customScopes, setCustomScopes] = useState([]);
  const [customScopesParameters, setCustomScopesParameters] = useState([]);
  const [scopeDefinition, setScopeDefinition] = useState(
    JSON.stringify({ include: {} }),
    null,
    2
  );
  const [newScopeName, setNewScopeName] = useState('');

  const [selectedComponent, setSelectedComponent] = useState();
  const [selectedComponentId, setSelectedComponentId] = useState();

  const [selectedScope, setSelectedScope] = useState();

  const [isEditMode, setEditMode] = useState(false);
  const [isCreateMode, setCreateMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRedirectModal, setshowRedirectModal] = useState(false);

  const customScopeId = useRef();

  const { loading: loadingComponents } = useQuery(GET_COMPONENTS, {
    onCompleted: (data) => {
      const implementedComponents = data.implementedWCFindMany
        .map((c) => ({ id: c._id, value: c.name, text: c.name }))
        .sort((a, b) => a.text.localeCompare(b.text));
      setComponents(implementedComponents);
    },
  });

  const [getCustomScopes, { refetch: refetchCustomScopes }] = useLazyQuery(
    GET_CUSTOM_SCOPES,
    {
      pollInterval: 15000,
      onCompleted: (data) => {
        const customScopes = data.testDefinitionFindMany
          .filter((c) => c.name && c.name !== '')
          .map((c) => ({ text: c.name, value: c._id }))
          .sort((a, b) => a.text.localeCompare(b.text));
        setCustomScopes(customScopes);
      },
    }
  );

  const [getParameters, { refetch: refetchBaseParams }] = useLazyQuery(
    GET_BASE_SCOPES_PARAMETERS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data?.testDefinitionFindOne) {
          setParameters(
            data.testDefinitionFindOne.parameters
              .filter((p) => typeof p.value === 'string')
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        } else {
          setParameters([]);
        }
      },
    }
  );

  const [getCustomScopesParameters, { refetch }] = useLazyQuery(
    GET_CUSTOM_SCOPES_PARAMETERS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data.testDefinitionFindOne) {
          setCustomScopesParameters(
            data.testDefinitionFindOne.parameters
              .filter((p) => typeof p.value === 'string')
              .sort((a, b) => a.name.localeCompare(b.name))
          );
          setScopeDefinition(
            JSON.stringify(data.testDefinitionFindOne.scope, null, 2)
          );
          customScopeId.current = data.testDefinitionFindOne._id;
        } else {
          setCustomScopesParameters([]);
          setScopeDefinition(JSON.stringify({}));
        }
      },
      onError: (error) => {},
    }
  );

  const [editCustomScope] = useMutation(EDIT_CUSTOM_SCOPE);
  const [createCustomScope] = useMutation(CREATE_CUSTOM_SCOPE);
  const [deleteCustomScope] = useMutation(DELETE_CUSTOM_SCOPE);

  useEffect(() => {
    if (!components.length) return;

    setSelectedComponent(components[0].value);
    setSelectedComponentId(components[0].id);
  }, [components]);

  useEffect(() => {
    if (!customScopes.length || !selectedScope) return;

    setSelectedScope(customScopes[0].value);
    customScopeId.current = customScopes[0].value;
    getCustomScopesParameters({
      variables: {
        component: selectedComponent,
        customScope: customScopeId.current,
      },
    });
  }, [customScopes]);

  // Refetch custom scopes when selected component changes
  useEffect(() => {
    if (!selectedComponent) return;

    getCustomScopes({ variables: { component: selectedComponent } });
    getParameters({
      variables: {
        component: selectedComponent,
        customScope: customScopeId.current,
      },
    });
  }, [selectedComponent, getCustomScopes, getParameters]);

  useEffect(() => {
    if (isCreateMode && customScopes.length > 0) {
      setSelectedScope(customScopes[0].value);
    }
  }, [isCreateMode, customScopes]);

  // Refetch custom scopes parameters when selected custom scope changes
  useEffect(() => {
    if (!selectedComponent || !selectedScope) return;
    customScopeId.current = customScopes.find(
      (cs) => cs.value === selectedScope
    )?.value;
    getCustomScopesParameters({
      variables: {
        component: selectedComponent,
        customScope: customScopeId.current,
      },
    });
  }, [selectedScope, selectedComponent, getCustomScopesParameters]);

  const changeComponentsHandler = (event) => {
    setSelectedComponent(event.value);
    const selectedItem = components.find((comp) => comp.value === event.value);
    setSelectedComponentId(selectedItem ? selectedItem.id : null);
    getCustomScopes({ variables: { component: event.value } });
    getParameters({
      variables: {
        component: event.value,
      },
    });
  };
  const changeScopesHandler = (event) => {
    setSelectedScope(event.value);
    getCustomScopesParameters({
      variables: {
        component: selectedComponent,
        customScope: event.value,
      },
    });
  };

  const selectHandler = (row) => {
    if (row.data.length) {
      const customScopes = [...customScopesParameters];
      setCustomScopesParameters(
        customScopes.concat(
          row.data.filter(
            (baseScope) =>
              !customScopes.some((param) => param.name === baseScope.name)
          )
        )
      );
    } else if (
      !customScopesParameters.some((param) => param.name === row.data.name)
    ) {
      setCustomScopesParameters([...customScopesParameters, row.data]);
    }
  };
  const deselectHandler = (row) => {
    if (row.isHeaderCheckboxClicked && row.data.length < parameters.length)
      return;

    if (row.data.length) {
      setCustomScopesParameters((params) =>
        params.filter(
          (p) =>
            !row.data.some((baseParameter) => baseParameter.name === p.name)
        )
      );
    } else
      setCustomScopesParameters((params) =>
        params.filter((p) => p.name !== row.data.name)
      );
  };
  const textEditor = (options) => {
    return (
      <InputText
        type='text'
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        className='w-100'
      />
    );
  };

  const onCellEditComplete = (e) => {
    const { rowData, newValue, field } = e;
    const index = customScopesParameters.findIndex(
      (param) => param.name === rowData.name
    );
    const newParams = [...customScopesParameters];
    newParams[index] = { ...rowData, [field]: newValue };
    setCustomScopesParameters(newParams);
  };

  const cancelCreateHandler = () => {
    setParameters([]);
    setCreateMode(false);
    refetch();
    refetchBaseParams();
  };

  const cancelEditHandler = () => {
    setCustomScopesParameters([]);
    setParameters([]);
    refetchBaseParams();
    refetch();
    setEditMode(false);
  };
  const onSubmitEdit = () => {
    window.showMessage('Loading', 'loading');
    const parameters = customScopesParameters.map((param) => {
      return { name: param.name, value: param.value };
    });
    editCustomScope({
      variables: {
        record: {
          operation: 'edit',
          name:
            customScopes.find((cs) => cs.value === selectedScope)?.text || '',
          parameters,
          scope: scopeDefinition,
          testDefinition: customScopeId.current,
          implementedWC: selectedComponentId,
        },
      },
    })
      .then(() => {
        setshowRedirectModal(true);
        refetch();
        setParameters([]);
        refetchBaseParams();
        setSelectedScope(
          customScopes.length > 0 ? customScopes[0].value : null
        );
        setEditMode(false);
        window.hideMessage();
      })
      .catch((error) => {
        window.hideMessage();
        window.flash(
          `An error occurred. Custom scope could not be updated. ${error.message}`,
          'error'
        );
      });
  };
  const onSubmitCreate = () => {
    window.showMessage('Loading', 'loading');

    const parameters = customScopesParameters.map((param) => {
      return { name: param.name, value: param.value };
    });
    createCustomScope({
      variables: {
        record: {
          operation: 'create',
          parameters,
          scope: scopeDefinition,
          implementedWC: selectedComponentId,
          name: newScopeName,
        },
      },
    })
      .then(() => {
        setshowRedirectModal(true);
        setParameters([]);
        refetchCustomScopes();
        refetch();
        refetchBaseParams();
        setCreateMode(false);
        window.hideMessage();
      })
      .catch((error) => {
        window.hideMessage();
        window.flash(
          `An error occurred. Custom scope could not be created. ${error.message}`,
          'error'
        );
        setNewScopeName('');
      });
  };

  const onSubmitDelete = () => {
    setShowDeleteModal(false);

    window.showMessage('Saving changes', 'loading');

    deleteCustomScope({
      variables: {
        record: {
          operation: 'delete',
          name: selectedScope,
          testDefinition: customScopeId.current,
          implementedWC: selectedComponentId,
        },
      },
    })
      .then(() => {
        setshowRedirectModal(true);
        refetchCustomScopes();
        refetchBaseParams();
        window.hideMessage();
      })
      .catch((error) => {
        window.hideMessage();
        window.flash(
          `An error occurred. Custom scope could not be deleted. ${error.message}`,
          'error'
        );
      });
  };
  const initCreateModeHandler = () => {
    setCreateMode(true);
    setScopeDefinition(JSON.stringify({ include: {} }, null, 2));
    setCustomScopesParameters([]);
  };

  const view = (
    <div className='d-flex justify-content-center'>
      <div className='e-card' style={{ width: '75%' }}>
        <div className='e-card-content m-2'>
          {!(isEditMode || isCreateMode) && (
            <div className='d-flex row rt-header-row align-items-center'>
              <div className='att-buttons-container col-sm-12'>
                <Button
                  onClick={initCreateModeHandler}
                  className='p-button-primary ml-2'
                  icon='pi pi-save'
                  label='Create'
                />
                <Button
                  onClick={() => setEditMode(true)}
                  className='p-button-outlined ml-2'
                  icon='pi pi-pencil'
                  label='Edit'
                />
                <Button
                  onClick={() => setShowDeleteModal(true)}
                  className='p-button-outlined ml-2'
                  icon='pi pi-trash'
                  label='Delete'
                />
              </div>
            </div>
          )}
          <div className='row rt-header-row align-items-center'>
            <div className='d-flex flex-column col-sm-12 col-md-6 control-section'>
              <div className='col-sm-12'>
                <span className='p-float-label'>
                  <Dropdown
                    id='component-dropdown'
                    options={components}
                    optionLabel='text'
                    optionValue='value'
                    value={selectedComponent}
                    onChange={changeComponentsHandler}
                    disabled={isEditMode || isCreateMode}
                    className='w-100'
                  />
                  <label htmlFor='component-dropdown'>Component</label>
                </span>
              </div>
              <div className='mt-3 col-sm-12'>
                {isCreateMode ? (
                  <span className='p-float-label'>
                    <InputText
                      id='scope-name'
                      value={newScopeName}
                      onChange={(e) => setNewScopeName(e.target.value)}
                      className='w-100'
                    />
                    <label htmlFor='scope-name'>Name</label>
                  </span>
                ) : (
                  <span className='p-float-label'>
                    <Dropdown
                      id='scopes-dropdown'
                      value={selectedScope}
                      options={customScopes}
                      optionLabel='text'
                      optionValue='value'
                      onChange={changeScopesHandler}
                      disabled={isEditMode}
                      className='w-100'
                    />
                    <label htmlFor='scopes-dropdown'>Scopes</label>
                  </span>
                )}
              </div>
            </div>
            <div className='d-flex flex-column col-sm-12 col-md-6 control-section att-scope-definition'>
              <label
                htmlFor='scope_definition'
                className='scope-definition-label'
              >
                Scope Definition
              </label>
              <Editor
                id='scope_definition'
                value={scopeDefinition}
                onValueChange={(code) => setScopeDefinition(code)}
                highlight={(code) => highlight(code, languages.js)}
                padding={10}
                className='code-editor'
                disabled={!(isEditMode || isCreateMode)}
                itemType={'json'}
              />
            </div>
          </div>
          <div className='row rt-header-row align-items-center'>
            <div className='mt-5 col-sm-12 col-md-6 control-section d-flex flex-column'>
              <label htmlFor='custom_scopes_parameters'>Parameters</label>
              <DataTable
                value={customScopesParameters}
                id='custom_scopes_parameters'
                className='editable-cells-table'
                scrollable
                scrollHeight='200px'
                editMode='cell'
              >
                <Column field='name' header='Key' style={{ width: '40%' }} />
                <Column
                  field='value'
                  header='Value'
                  style={{ width: '60%' }}
                  editor={textEditor}
                  onCellEditComplete={onCellEditComplete}
                />
              </DataTable>
            </div>
            <div className='mt-5 ml-auto col-sm-12 col-md-6 control-section d-flex flex-column'>
              <BaseScopeParametersGrid
                parameters={parameters}
                customScopesParameters={customScopesParameters}
                deselectHandler={deselectHandler}
                isCreateMode={isCreateMode}
                isEditMode={isEditMode}
                selectHandler={selectHandler}
              />
              <CustomModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onAccept={onSubmitDelete}
                title='Are you sure you want to delete this custom scope?'
              />
              <CustomModal
                show={showRedirectModal}
                onClose={() => setshowRedirectModal(false)}
                onAccept={() =>
                  (window.location.href = '/custom-scopes-processing-queue')
                }
                title='The custom scope has been sent to a queue for processing. Do you want to be redirected to the processing view?'
              />
            </div>
          </div>
          {!(isEditMode || isCreateMode) && (
            <div className='d-flex mt-5'>
              <div className='ml-auto'>
                <Button
                  onClick={() => (window.location.href = '/trfx-run-tests')}
                  className='p-button-outlined mr-1'
                  icon='pi pi-times'
                  label='Close'
                />
                <Button
                  onClick={() =>
                    (window.location.href = '/custom-scopes-processing-queue')
                  }
                  className='p-button-primary'
                  icon='pi pi-cog'
                  label='Processing queue'
                />
              </div>
            </div>
          )}
          {isEditMode && (
            <div className='d-flex mt-5'>
              <div className='ml-auto'>
                <Button
                  className='p-button-outlined mr-2'
                  onClick={cancelEditHandler}
                  label='Cancel'
                />
                <Button
                  className='p-button-primary'
                  onClick={onSubmitEdit}
                  label='Save'
                />
              </div>
            </div>
          )}
          {isCreateMode && (
            <div className='d-flex mt-5'>
              <div className='ml-auto'>
                <Button
                  className='p-button-outlined mr-2'
                  onClick={cancelCreateHandler}
                  label='Cancel'
                />
                <Button
                  className='p-button-primary'
                  onClick={onSubmitCreate}
                  label='Save'
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return <GraphQLWrapper loading={loadingComponents} component={view} />;
}
export default ViewCustomScopes;
